import React, { memo, useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import sanitizeHtml from 'sanitize-html';
import { Button, Chip, Icon, Modal, Text, useTheme } from '@Sage/hoi-poi-ui';

import { BillboardActions, EntityDetailActions } from 'actions';
import Context from 'managers/Context';
import { BILLBOARD } from 'constants/Entities';
import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';

function getExtension(fileName = '') {
    const parts = fileName.split('.');
    return parts.length > 1 ? parts[parts.length - 1] : '';
}

function truncateFileName(name) {
    if (!name || name.length <= 11) return name;
    return `${name.substring(0, 3)}...${name.substring(name.length - 8)}`;
}

function getDescriptionHtml(data) {
    if (typeof data?.description === 'object') {
        return data.description.html || '';
    }
    return data?.description || '';
}

function parseEnvironmentIds(environmentsId) {
    return (environmentsId || '').split(';').filter((value) => value !== '');
}

function getConfirmMeaningLiteral(loggedUserName) {
    let literal = getLiteral('label_billboard_confirm_meaning');
    if (literal.includes('%@')) {
        literal = literal.replace('%@', loggedUserName);
    }
    return literal;
}

const PreviewTopBar = memo(({ onClose, subject }) => {
    const theme = useTheme();

    return (
        <header className="billboard-preview__header">
            <Icon
                className="billboard-preview__close"
                color={theme.colors.primary.white}
                name="close"
                onClick={onClose}
                size="medium"
                tooltip={getLiteral('action_close')}
            />
            <Text color={theme.colors.primary.white} type="body1">
                {getLiteral('title_billboard_preview')}
            </Text>
            <Icon
                className="billboard-preview__breadcrumb-icon"
                color={theme.colors.primary.white}
                name="chevron"
                size="small"
            />
            <Text
                className="billboard-preview__breadcrumb-title"
                color={theme.colors.primary.white}
                isTruncated
                type="body1"
                useTooltip
            >
                {subject}
            </Text>
        </header>
    );
});

PreviewTopBar.displayName = 'PreviewTopBar';

PreviewTopBar.propTypes = {
    onClose: PropTypes.func.isRequired,
    subject: PropTypes.string,
};

const PreviewEnvironmentChips = memo(({ environmentIds, environmentsList }) => {
    const chips = useMemo(
        () =>
            environmentIds
                .map((environmentId) => {
                    const environment = environmentsList.find(
                        (item) => String(item['-id']) === String(environmentId),
                    );
                    if (!environment) return null;

                    return (
                        <Chip
                            key={environmentId}
                            className="billboard-preview__environment-chip"
                            isFilled
                            isReadOnly
                            size="small"
                        >
                            {environment.descripcion}
                        </Chip>
                    );
                })
                .filter(Boolean),
        [environmentIds, environmentsList],
    );

    if (chips.length === 0) return null;

    return <div className="billboard-preview__chips">{chips}</div>;
});

PreviewEnvironmentChips.displayName = 'PreviewEnvironmentChips';

PreviewEnvironmentChips.propTypes = {
    environmentIds: PropTypes.arrayOf(PropTypes.string).isRequired,
    environmentsList: PropTypes.array.isRequired,
};

const PreviewFileItem = memo(({ file, isEdition }) => {
    const extension = getExtension(file.name || file.description || '');
    const fullName = isEdition ? `${file.description}.${extension}` : file.name || file.description;
    const showName = truncateFileName(fullName);

    return (
        <div className="billboard-preview__file">
            <span className="billboard-preview__file-extension">
                <Text bold type="caption">
                    {extension.toUpperCase()}
                </Text>
            </span>
            <div className="billboard-preview__file-info">
                <Text
                    className="billboard-preview__file-name"
                    isTruncated
                    type="caption"
                    useTooltip
                >
                    {showName}
                </Text>
            </div>
        </div>
    );
});

PreviewFileItem.displayName = 'PreviewFileItem';

PreviewFileItem.propTypes = {
    file: PropTypes.object.isRequired,
    isEdition: PropTypes.bool,
};

const PreviewMobileMock = memo(({ data, descriptionHtml, files, loggedUserName }) => {
    const theme = useTheme();
    const isEdition = !!data?.id;
    const hasFiles = files.length > 0;
    const confirmMeaning = useMemo(
        () => getConfirmMeaningLiteral(loggedUserName),
        [loggedUserName],
    );

    return (
        <div className="billboard-preview__mobile">
            <div
                className={`billboard-preview__mobile-content${
                    isEdition ? '' : ' billboard-preview__mobile-content--with-footer'
                }`}
            >
                <div className="billboard-preview__mobile-header">
                    <Text color={theme.colors.utility.textSecondary} type="caption">
                        {`${getLiteral('label_from')}: ${loggedUserName}`}
                    </Text>
                    <Text
                        as="div"
                        bold
                        className="billboard-preview__mobile-title"
                        isTruncated
                        type="subtitleLg"
                        useTooltip
                    >
                        {data?.subject}
                    </Text>
                    <div className="billboard-preview__mobile-meta">
                        <Text color={theme.colors.utility.textSecondary} type="caption">
                            {Context.utilsFormats.getNowMoment('LT')}
                        </Text>
                        <div className="billboard-preview__mobile-attachments">
                            <Text color={theme.colors.utility.textSecondary} type="caption">
                                {getLiteralWithParameters('label_billboard_attached', [
                                    files.length.toString(),
                                ])}
                            </Text>
                            <Icon
                                color={theme.colors.utility.textSecondary}
                                name="attachFile"
                                size="small"
                            />
                        </div>
                    </div>
                </div>

                {hasFiles ? (
                    <div className="billboard-preview__files mobile-scroll">
                        {files.map((file, index) => (
                            <PreviewFileItem
                                key={`${file.name || file.description}-${index}`}
                                file={file}
                                isEdition={isEdition}
                            />
                        ))}
                    </div>
                ) : null}

                <div
                    className={`billboard-preview__description mobile-scroll${
                        hasFiles ? '' : ' billboard-preview__description--expanded'
                    }`}
                >
                    <div
                        className="ql-editor billboard-preview__description-text"
                        dangerouslySetInnerHTML={{
                            __html: sanitizeHtml(descriptionHtml),
                        }}
                    />
                </div>

                {!isEdition ? (
                    <footer className="billboard-preview__mobile-footer">
                        <Text
                            className="billboard-preview__mobile-footer-text"
                            color={theme.colors.utility.textSecondary}
                            type="body"
                        >
                            {confirmMeaning}
                        </Text>
                        <Button
                            className="billboard-preview__mobile-footer-button"
                            isDisabled
                            size="small"
                            type="primary"
                        >
                            {getLiteral('action_billboard_confirm')}
                        </Button>
                    </footer>
                ) : null}
            </div>
        </div>
    );
});

PreviewMobileMock.displayName = 'PreviewMobileMock';

PreviewMobileMock.propTypes = {
    data: PropTypes.object,
    descriptionHtml: PropTypes.string,
    files: PropTypes.array.isRequired,
    loggedUserName: PropTypes.string.isRequired,
};

const PreviewSidebar = memo(({ confirmButton, environmentIds, environmentsList }) => {
    const theme = useTheme();

    return (
        <aside className="billboard-preview__sidebar">
            <div className="billboard-preview__sidebar-content">
                <div className="billboard-preview__sidebar-title">
                    <Text color={theme.colors.primary.white} type="h6">
                        {getLiteral('title_billboard_preview')}
                    </Text>
                    <div className="billboard-preview__title-separator" />
                </div>
                <Text color={theme.colors.primary.white} type="body1">
                    {getLiteral('label_billboard_preview')}
                </Text>
                <PreviewEnvironmentChips
                    environmentIds={environmentIds}
                    environmentsList={environmentsList}
                />
                {confirmButton}
            </div>
        </aside>
    );
});

PreviewSidebar.displayName = 'PreviewSidebar';

PreviewSidebar.propTypes = {
    confirmButton: PropTypes.node.isRequired,
    environmentIds: PropTypes.arrayOf(PropTypes.string).isRequired,
    environmentsList: PropTypes.array.isRequired,
};

const BillboardPreview = memo(({ data, loadingSave, onSave }) => {
    const dispatch = useDispatch();
    const [resendConfirmOpen, setResendConfirmOpen] = useState(false);

    const environmentsList = useSelector((state) => {
        const list = state.valueList.get('tblSucursalesBillboard');
        return list ? list.get('data') : null;
    });
    const loggedUserName = useSelector(
        (state) => `${state.config.userData.nombre} ${state.config.userData.apellidos}`,
    );
    const newFilesImmutable = useSelector((state) => state.billboard.crud.get('files'));
    const filesToRemoveImmutable = useSelector((state) =>
        state.billboard.crud.get('filesToRemove'),
    );

    const newFiles = useMemo(() => newFilesImmutable.toJS(), [newFilesImmutable]);
    const filesToRemove = useMemo(() => filesToRemoveImmutable.toJS(), [filesToRemoveImmutable]);
    const environmentsListJson = useMemo(
        () => environmentsList?.toJSON() ?? [],
        [environmentsList],
    );

    const files = useMemo(() => {
        let existingFiles = data?.attachmentFiles || [];
        filesToRemove.forEach((idFile) => {
            existingFiles = existingFiles.filter((file) => file.id !== idFile);
        });
        return [...existingFiles, ...newFiles];
    }, [data?.attachmentFiles, filesToRemove, newFiles]);

    const environmentIds = useMemo(
        () => parseEnvironmentIds(data?.environmentsId),
        [data?.environmentsId],
    );
    const descriptionHtml = useMemo(() => getDescriptionHtml(data), [data]);

    const closePreview = useCallback(() => {
        if (loadingSave) return;
        if (data?.id) {
            dispatch(EntityDetailActions.open(BILLBOARD, data.id));
        }
        dispatch(BillboardActions.setBillboardPreview(false));
    }, [data?.id, dispatch, loadingSave]);

    const handleSave = useCallback(() => {
        if (loadingSave) return;
        onSave?.();
    }, [loadingSave, onSave]);

    const handleResendConfirm = useCallback(() => {
        if (loadingSave) return;
        setResendConfirmOpen(false);
        onSave?.();
    }, [loadingSave, onSave]);

    const handleOpenResendConfirm = useCallback(() => {
        if (loadingSave) return;
        setResendConfirmOpen(true);
    }, [loadingSave]);

    const confirmButton = data?.id ? (
        <Button
            className="billboard-preview__confirm"
            isDisabled={loadingSave}
            isLoading={loadingSave}
            onClick={handleOpenResendConfirm}
            size="big"
            type="primary"
        >
            {getLiteral('action_billboard_resend')}
        </Button>
    ) : (
        <Button
            className="billboard-preview__confirm"
            isDisabled={loadingSave}
            isLoading={loadingSave}
            onClick={handleSave}
            size="big"
            type="primary"
        >
            {getLiteral('action_billboard_post')}
        </Button>
    );

    return createPortal(
        <div className="billboard-crud-preview">
            <PreviewTopBar onClose={closePreview} subject={data?.subject} />

            <div className="billboard-preview__body">
                <PreviewSidebar
                    confirmButton={confirmButton}
                    environmentIds={environmentIds}
                    environmentsList={environmentsListJson}
                />
                <div className="billboard-preview__mockup">
                    <PreviewMobileMock
                        data={data}
                        descriptionHtml={descriptionHtml}
                        files={files}
                        loggedUserName={loggedUserName}
                    />
                </div>
            </div>

            <Modal
                cancelText={getLiteral('action_billboard_no')}
                confirmText={getLiteral('action_billboard_yes')}
                isConfirmDisabled={loadingSave}
                isOpen={resendConfirmOpen}
                onCancel={() => !loadingSave && setResendConfirmOpen(false)}
                onConfirm={handleResendConfirm}
                onRequestClose={() => !loadingSave && setResendConfirmOpen(false)}
                overlayClassName="billboard-preview__confirm-modal-overlay"
                size="small"
                title={getLiteral('warning_billboard_resend_title')}
            >
                <Text className="billboard-preview__resend-warning" type="body">
                    {getLiteral('warning_billboard_resend_message_1')}
                </Text>
                <Text type="body">{getLiteral('warning_billboard_resend_message_2')}</Text>
                <Text type="body">{getLiteral('warning_billboard_resend_message_3')}</Text>
            </Modal>
        </div>,
        document.body,
    );
});

BillboardPreview.displayName = 'BillboardPreview';

BillboardPreview.propTypes = {
    data: PropTypes.object,
    loadingSave: PropTypes.bool,
    onSave: PropTypes.func,
};

export default BillboardPreview;
